import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NothingToShow, PageLayout, TopBar } from 'shared-ui';

import { ObecnoscStrefa } from 'api/strefy/strefy.types';
import { ReactComponent as CollapseIcon } from 'assets/icons/collapse.svg';
import { ReactComponent as ExpandIcon } from 'assets/icons/expand.svg';
import { getExpandableZoneIds, toggleZoneSubtree } from 'app/definitions/zones/Zones.utils';
import { EXPAND_ORGANIZATION_ITEM_TAGS } from 'app/definitions/organization/Organization.variables';
import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { AppMessages } from 'i18n/messages';
import { AppRoute, AppRouteWithParams } from 'routing/AppRoute.enum';
import { Loader } from 'ui/loader/Loader';

import { CurrentPresenceHeader } from './currentPresenceHeader/CurrentPresenceHeader';
import { PresenceZoneItem } from './presenceZoneItem/PresenceZoneItem';
import * as Styled from './CurrentPresence.styles';
import { PresenceRefreshSelect } from './presenceRefreshSelect/PresenceRefreshSelect';
import { CurrentPresenceProps, PresenceLocationState } from './CurrentPresence.types';

export const CurrentPresence = ({ presence, isLoading, refetchInterval, setRefetchInterval }: CurrentPresenceProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [isInitiallyExpanded, setIsInitiallyExpanded] = useState(false);

  const zones = presence?.strefy ?? [];
  const canViewEvents = !!perms?.ZDARZENIA?.view;
  const canViewOtherEvents = !!perms?.ZDARZENIA_INNE?.view;
  const tabsCount = 1 + (canViewEvents ? 2 : 0) + (canViewOtherEvents ? 1 : 0);

  const expandableZoneIds = useMemo(() => getExpandableZoneIds(zones), [zones]);
  const isAllNodesExpanded = expandableZoneIds.length > 0 && expandableZoneIds.every((id) => expanded.includes(id));

  // Expand everything once; later polls keep whatever the operator collapsed.
  useEffect(() => {
    if (isInitiallyExpanded || !zones.length) return;

    setExpanded(expandableZoneIds);
    setIsInitiallyExpanded(true);
  }, [zones]);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    if (EXPAND_ORGANIZATION_ITEM_TAGS.includes((event.target as HTMLElement)?.tagName)) {
      setExpanded(toggleZoneSubtree(zones, expanded, nodeIds));
    }
  };

  const handleShowOwners = (zone: ObecnoscStrefa) => {
    const state: PresenceLocationState = { refetchInterval };

    navigate(AppRouteWithParams.eventsCurrentPresenceOwners.get({ id: zone.id }), { state });
  };

  const handleToggleAll = () => {
    setExpanded(isAllNodesExpanded ? [] : expandableZoneIds);
  };

  const renderTree = () => {
    if (isLoading) return <Loader />;

    if (!zones.length) {
      return <NothingToShow text={formatMessage({ id: AppMessages['events.currentPresence.nothingToShow'] })} />;
    }

    return (
      <>
        <CurrentPresenceHeader isAllNodesExpanded={isAllNodesExpanded} handleToggleAll={handleToggleAll} />
        <Styled.TreeView
          expanded={expanded}
          aria-label="presence-zones-label"
          disableSelection
          // Design: ▲ on an expanded zone, ▼ on a collapsed one.
          defaultCollapseIcon={<CollapseIcon />}
          defaultExpandIcon={<ExpandIcon />}
          onNodeToggle={handleToggle}
        >
          {zones.map((zone) => (
            <PresenceZoneItem key={zone.id} zone={zone} onShowOwners={handleShowOwners} />
          ))}
        </Styled.TreeView>
      </>
    );
  };

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['events.title'] })}
      renderContent={() => (
        <>
          <Styled.TopBarWrapper gridTemplateColumns={`repeat(${tabsCount}, auto) 1fr`}>
            {canViewEvents && (
              <>
                <TopBar.Link linkTo={AppRoute.events} isActive={false}>
                  {formatMessage({ id: AppMessages['events.current'] })}
                </TopBar.Link>

                <TopBar.Link linkTo={AppRoute.eventsHistory} isActive={false}>
                  {formatMessage({ id: AppMessages['events.history'] })}
                </TopBar.Link>
              </>
            )}

            {canViewOtherEvents && (
              <TopBar.Link linkTo={AppRoute.eventsOther} isActive={false}>
                {formatMessage({ id: AppMessages['events.other'] })}
              </TopBar.Link>
            )}

            <TopBar.Link linkTo={AppRoute.eventsCurrentPresence} isActive>
              {formatMessage({ id: AppMessages['events.currentPresence'] })}
            </TopBar.Link>

            <TopBar.ButtonsWrapper>
              <></>
            </TopBar.ButtonsWrapper>
          </Styled.TopBarWrapper>

          <Styled.ToolbarWrapper>
            <PresenceRefreshSelect refetchInterval={refetchInterval} setRefetchInterval={setRefetchInterval} />
          </Styled.ToolbarWrapper>

          {renderTree()}
        </>
      )}
    />
  );
};
